import { setChannelHeader } from "@/utils/headers";
import { createRouter, createWebHistory } from "vue-router";
import { ChannelPermission } from "@/generated/graphql";
import {
  defaultAppChannel,
  getDefaultDomainChannel,
  isDomainSpecificChannel,
} from "@/utils/channel";
import ChannelProvider from "../components/channel-provider.vue";
import ChannelLayout from "../components/channel-layout.vue";
import Channel from "../components/channel/channel.vue";

export enum RouteNames {
  Home = "Home",
  ChannelList = "ChannelList",
  Referral = "Referral",

  Channel = "Channel",

  EditQuiz = "EditQuiz",
  EditRaffle = "EditRaffle",

  Show = "Show",
  NewShow = "NewShow",

  ShowList = "Shows",

  Profile = "Profile",
  ProfileHostView = "ProfileHostView",
  ProfileFanView = "ProfileFanView",
  ProfileMembership = "ProfileMembership",
  ProfileEdit = "ProfileEdit",
  ProfileNotifications = "ProfileNotifications",
  ProfilePrivacy = "ProfilePrivacy",
  ProfileMedia = "ProfileMedia",
  ProfileEvents = "ProfileEvents",
  ProfilePurchases = "ProfilePurchases",

  ThreadsNew = "ThreadsNew",
  ThreadsEdit = "ThreadsEdit",
  ThreadsItem = "ThreadsItem",

  Dashboard = "Dashboard",
  DashboardHome = "DashboardHome",

  DashboardUserList = "DashboardUserList",
  DashboardUserItem = "DashboardUserItem",

  DashboardShowList = "DashboardShowList",
  DashboardShowItem = "DashboardShowItem",

  DashboardTopicList = "DashboardTopicList",
  DashboardTopicItem = "DashboardTopicItem",

  DashboardShoppingProductList = "DashboardShoppingProductList",

  DashboardVideoList = "DashboardVideoList",
  DashboardVideoItem = "DashboardVideoItem",

  DashboardPollList = "DashboardPollList",

  Leaderboard = "Leaderboard",
  AdminLeaderboard = "AdminLeaderboard",

  UserManagement = "UserManagement",
  BehaviouralDataBrowser = "BehaviouralDataBrowser",
  RewardList = "RewardList",
  BdbRecap = "BdbRecap",
  PrivacyPolicy = "PrivacyPolicy",
  TermsOfService = "TermsOfService",
  AccessibilityStatement = "AccessibilityStatement",
  ApiKeyManagement = "ApiKeyManagement",

  SsoManagement = "SsoManagement",
  SsoProviders = "SsoProviders",
  SsoWebApps = "SsoWebApps",
  SsoCatalogs = "SsoCatalogs",
  SsoReleases = "SsoReleases",

  OauthCallback = "OauthCallback",

  ChannelAuth = "ChannelAuth",

  CustomizeChannel = "CustomizeChannel",

  ManageOffers = "ManageOffers",
  CreateOffer = "CreateOffer",
  EditOffer = "EditOffer",
}

const shouldAllowDynamicChannel = !isDomainSpecificChannel(
  window.location.host,
);

export const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes: [
    {
      path: "/auth/callback",
      name: RouteNames.OauthCallback,
      strict: true,
      component: () => import("@/pages/auth/oauth-callback.vue"),
    },
    {
      path: "/",
      name: "Channel Provider",
      component: ChannelProvider,
      beforeEnter: (to, _from, next) => {
        const routeChannelName = Array.isArray(to.params.channel)
          ? to.params.channel[0]
          : to.params.channel;

        const domain = window.location.host;
        const defaultAppChannel = getDefaultDomainChannel(domain);

        // If the route is the root, use the default channel per the alias in the route
        const channelName =
          to.path === "/" ? defaultAppChannel : routeChannelName;

        if (channelName) setChannelHeader(channelName);
        next();
      },
      children: [
        {
          path: "/",
          component: ChannelLayout,
          children: [
            {
              path: shouldAllowDynamicChannel ? "/:channel?" : "",
              alias: "",
              name: RouteNames.Channel,
              component: Channel,
            },

            {
              path: "/:channel",
              children: [
                {
                  path: "edit/:show/:type?",
                  name: RouteNames.NewShow,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageShows,
                  },
                  component: () =>
                    import("@/components/show/show-creation/wizard.vue"),
                },
                {
                  path: "shows",
                  name: RouteNames.ShowList,
                  component: () =>
                    import("@/components/channel/shows/show-list.vue"),
                },
                {
                  path: "threads/:thread_id",
                  name: RouteNames.ThreadsItem,
                  component: () =>
                    import("@/components/channel/threads/threads-item.vue"),
                },
                {
                  path: "threads/new",
                  name: RouteNames.ThreadsNew,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageThreads,
                  },
                  component: () =>
                    import("@/components/channel/threads/threads-new.vue"),
                },
                {
                  path: "threads/edit/:threadId",
                  name: RouteNames.ThreadsEdit,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageThreads,
                  },
                  component: () =>
                    import("@/components/channel/threads/threads-edit.vue"),
                },
                {
                  path: "edit/raffle/:raffle",
                  name: RouteNames.EditRaffle,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageContests,
                  },
                  component: () =>
                    import("../components/channel/contests/creation/raffle-creation.vue"),
                },
                {
                  path: "edit/quiz/:quiz",
                  name: RouteNames.EditQuiz,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageContests,
                  },
                  component: () =>
                    import("../components/channel/contests/creation/quiz-creation.vue"),
                },
                {
                  path: "leaderboard",
                  component: () =>
                    import("@/pages/leaderboard/leaderboard.layout.vue"),
                  children: [
                    {
                      path: "",
                      name: RouteNames.Leaderboard,
                      component: () =>
                        import("@/pages/leaderboard/leaderboard.page.vue"),
                    },
                  ],
                },
                {
                  path: "profile",
                  name: RouteNames.Profile,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () => import("@/pages/profile/profile.layout.vue"),
                },
                {
                  path: "profile/membership",
                  name: RouteNames.ProfileMembership,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/manage-membership.page.vue"),
                },
                {
                  path: "profile/edit",
                  name: RouteNames.ProfileEdit,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-edit.page.vue"),
                },
                {
                  path: "profile/notifications",
                  name: RouteNames.ProfileNotifications,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-notifications.page.vue"),
                },
                {
                  path: "profile/privacy",
                  name: RouteNames.ProfilePrivacy,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-privacy.page.vue"),
                },
                {
                  path: "profile/media",
                  name: RouteNames.ProfileMedia,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-media-page.vue"),
                },
                {
                  path: "profile/events",
                  name: RouteNames.ProfileEvents,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-events.page.vue"),
                },
                {
                  path: "profile/purchases",
                  name: RouteNames.ProfilePurchases,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () =>
                    import("@/pages/profile/profile-purchases.page.vue"),
                },
                {
                  path: "profile/fan-view/:profileId",
                  name: RouteNames.ProfileFanView,
                  meta: {
                    requiresAuth: true,
                  },
                  component: () => import("@/pages/profile/fan-view.page.vue"),
                },
                {
                  path: "user-management",
                  name: RouteNames.UserManagement,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageChannel,
                  },
                  component: () =>
                    import("../components/channel/user-management/user-management.vue"),
                },
                {
                  path: "api-keys",
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageChannel,
                  },
                  component: () =>
                    import("../pages/api-key-management/api-key-management.layout.vue"),
                  children: [
                    {
                      path: "",
                      name: RouteNames.ApiKeyManagement,
                      component: () =>
                        import("../pages/api-key-management/api-key-management.page.vue"),
                    },
                  ],
                },
                {
                  path: "admin-leaderboard",
                  name: RouteNames.AdminLeaderboard,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageChannel,
                  },
                  component: () =>
                    import("../pages/admin-leaderboard/admin-leaderboard.page.vue"),
                },
                {
                  path: "sso",
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageChannel,
                  },
                  component: () =>
                    import("@/pages/sso-management/sso-management.layout.vue"),
                  children: [
                    {
                      path: "",
                      redirect: { name: RouteNames.SsoCatalogs },
                    },
                    {
                      path: "providers",
                      name: RouteNames.SsoProviders,
                      component: () =>
                        import("../pages/sso-management/sso-providers.page.vue"),
                    },
                    {
                      path: "web-apps",
                      name: RouteNames.SsoWebApps,
                      component: () =>
                        import("@/pages/sso-management/sso-web-apps.page.vue"),
                    },
                    {
                      path: "catalogs",
                      name: RouteNames.SsoCatalogs,
                      component: () =>
                        import("@/pages/sso-management/sso-catalogs.page.vue"),
                    },
                    {
                      path: "catalogs/:catalogId/releases",
                      name: RouteNames.SsoReleases,
                      component: () =>
                        import("@/pages/sso-management/sso-releases.page.vue"),
                    },
                  ],
                },
                {
                  path: "offers",
                  name: RouteNames.ManageOffers,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageOffers,
                  },
                  component: () =>
                    import("../pages/channel-offers/channel-offers.page.vue"),
                },
                {
                  path: "offers/new",
                  name: RouteNames.CreateOffer,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageOffers,
                  },
                  component: () =>
                    import("../pages/channel-offers/offer-form.page.vue"),
                },
                {
                  path: "offers/edit/:offerId",
                  name: RouteNames.EditOffer,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageOffers,
                  },
                  component: () =>
                    import("../pages/channel-offers/offer-form.page.vue"),
                },
                {
                  path: "customize",
                  name: RouteNames.CustomizeChannel,
                  meta: {
                    requiresAuth: true,
                    requiresPermission: ChannelPermission.ManageChannel,
                  },
                  component: () =>
                    import("../pages/customize-channel/customize-channel.layout.vue"),
                },
              ],
            },
          ],
        },
        {
          path: "/",
          component: () => import("@/components/channel-clean-layout.vue"),
          children: [
            {
              path: "/:channel/auth",
              name: RouteNames.ChannelAuth,
              component: () =>
                import("@/pages/auth-modal/standalone-auth.page.vue"),
            },
            {
              path: "/:channel/ln/:code",
              name: RouteNames.Referral,
              component: () =>
                import("@/features/channel/referral/referral-page.vue"),
            },
            {
              path: "/:channel/:show",
              name: RouteNames.Show,
              component: () => import("@/features/show/show.vue"),
            },
            {
              path: "/:channel/bdb",
              component: () =>
                import("@/components/channel/bdb/bdb-layout.vue"),
              meta: {
                requiresAuth: true,
                requiresPermission: ChannelPermission.ManageChannel,
              },
              children: [
                {
                  path: "",
                  name: RouteNames.BehaviouralDataBrowser,
                  component: () =>
                    import("@/components/channel/bdb/bdb-base.vue"),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "/privacy-policy",
      name: RouteNames.PrivacyPolicy,
      component: () => import("@/pages/gdpr/privacy-policy.vue"),
    },
    {
      path: "/terms-of-service",
      name: RouteNames.TermsOfService,
      component: () => import("@/pages/gdpr/terms-of-service.vue"),
    },
    {
      path: "/accessibility-statement",
      name: RouteNames.AccessibilityStatement,
      component: () => import("@/pages/gdpr/accessibility-statement.vue"),
    },
  ],
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
        block: "center",
      };
    } else {
      return { top: 0 };
    }
  },
});

export function getChannelNameFromRoute(path: string): string | null {
  const route = router.resolve(path);

  const value = route.params.channel;

  if (value && value instanceof Array) {
    return value[0] ?? null;
  }

  return value ?? null;
}

export function isChannelRoute(path: string) {
  return getChannelNameFromRoute(path) !== null;
}

export function getExpectedChannelFromPath(
  pathname: string,
  channelParam?: string,
): string | null {
  const shouldRenderFanFest = pathname === "/";

  if (shouldRenderFanFest) return defaultAppChannel;

  const channelFromPath = pathname.split("/")[1];

  if (isChannelRoute(pathname)) return channelParam ?? channelFromPath ?? null;

  // Always return at least the default channel if route params aren't ready yet
  return defaultAppChannel;
}
